'use client';

// Хост боковых слоёв-плашек приложения (план NATIVE-SLIDE): натив двигает ВЕСЬ webHost, поэтому
// содержимое каждого выезжающего слоя — полноэкранная плашка в DOM кабинета. Здесь — стопка
// таких плашек: push кладёт новую (натив снимает предыдущий экран и вдвигает), pop снимает
// верхнюю (родной выезд назад), update меняет шапку/тело верхней на месте (внутренняя навигация
// плашки, как в drawer). Каждая плашка — полноэкранная, непрозрачная; натив сам её инсетит/двигает.
//
// Показываем ТОЛЬКО верхнюю плашку (нижние натив держит замороженными снимками); прячем всё,
// когда открыт сценарий-МАРШРУТ (focus) — там верхний слой это сам сценарий, поверх плашек.
// В браузере/старых сборках (нет нативных слоёв) хост не используется — поверхности берут drawer.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  hasNativeLayers,
  nextLayerTag,
  popLayer,
  pushLayer,
  subscribeTopLayer,
  topLayerTag,
} from '@/lib/native-layers';
import { nativeBridge, nativeMethod } from '@/lib/native-app';
import { useDrawer, useDrawerOptional, type DrawerPayload } from './drawer';
import { KBareButton } from '@scriptera/ui/kit';

export interface SlidePanel {
  title: string;
  /** Серая строка под заголовком (напр. «Шаг 1 из 3» мастера шаблона, владелец 05.08).
   *  DOM-шапка рисует сама; нативному верху ключ уезжает в configureSheetHeader —
   *  старые сборки его игнорируют (строка появится со сборкой Xcode). */
  subtitle?: string;
  body: React.ReactNode;
  /** Внутренняя «назад» плашки (напр. категория каталога) — стрелка вместо × и без снятия слоя. */
  onBack?: () => void;
  /** Элемент в правом слоте шапки (напр. кнопка «Информация» у карточки модели). */
  headerRight?: React.ReactNode;
  /** Элемент в ЛЕВОМ слоте шапки вместо ведущей кнопки (× / ‹) — слово «Выбрать всё»
   *  экрана «Кто получит» (И9.1). Нативный верх слова слева не умеет (`lead` знает
   *  только 'back' и 'close'), поэтому у такой плашки шапка остаётся DOM-овой — ровно
   *  тем же правилом, что и у невыразимого headerRight. */
  headerLeft?: React.ReactNode;
  /** Кнопка-действие справа для НАТИВНОГО верха шторки: текст (✓/«Готово»/«Фильтры») ИЛИ
   *  SF-иконка (actionIcon, напр. info.circle у карточки модели). headerRight остаётся
   *  DOM-фолбэком старых сборок. */
  actionLabel?: string;
  actionIcon?: string;
  onAction?: () => void;
  /** РОДНОЕ меню кнопки-действия (UIMenu, iOS-«селект»): пункты вместо одиночного тапа.
   *  Есть меню → тап кнопки открывает его, onAction у НОВЫХ сборок не зовётся (старые
   *  сборки меню не знают — шлют sheetAction, onAction остаётся фолбэком).
   *  sub — серая подпись под пунктом (subtitle UIAction; напр. адрес почты).
   *  section/sectionTitle — инлайн-группы UIMenu: соседние пункты с одним section идут
   *  одной группой с разделителем и серым заголовком (фильтр раздела в шторке). */
  actionMenu?: Array<{
    key: string;
    label: string;
    icon?: string;
    sub?: string;
    checked?: boolean;
    section?: string;
    sectionTitle?: string;
  }>;
  onMenu?: (key: string) => void;
  /** ВТОРАЯ кнопка справа в нативной шапке слоя (владелец 2026-08-07): у раздела с «+»
   *  фильтр обязан стоять В ШАПКЕ рядом с плюсом, а не болтаться кружком под ней.
   *  Только SF-иконка. Старые сборки ключа не знают — спрашивать их поддержку надо
   *  через nativeMethod('sheetHeaderAction2'), иначе кнопка потеряется совсем. */
  action2Icon?: string;
  action2Label?: string;
  onAction2?: () => void;
  /** Мелкая иконка перед заголовком нативного верха (PNG base64 — логотип модели). */
  titleIconPng?: string;
  /** Нативные вкладки под шапкой листа (режимы модели; наборы любые). Тап → onTab(key). */
  tabs?: Array<{ key: string; label: string }>;
  selectedTab?: string;
  onTab?: (key: string) => void;
  /** Зовётся, когда слой СНЯТ любым способом (× / свайп / pop) — для сброса состояния владельца
   *  слоя (напр. флаг «редактор открыт» у одно-слойного редактора со сменным содержимым). */
  onDismiss?: () => void;
  /** Слой — уровень ОКНА КОНСТРУКТОРА (горизонтальное окно со стеком уровней, владелец
   *  13.08.2026). Меняет ТОЛЬКО ведущую кнопку шапки: КОРНЕВОЙ уровень такого окна
   *  закрывается КРЕСТИКОМ («✕ только на корне», решение 13.08 — новее и конкретнее
   *  общего правила 23.07 «боковой слой = скобка»), вложенные уровни — скобка ‹ (назад на
   *  уровень выше). Прочие боковые слои (навигация меню, карточки разделов, пикеры) флага
   *  не ставят и живут прежней скобкой. Поле ВЕБ-стороны: в мост уезжает существующим
   *  ключом lead ('close' | 'back'), новых методов/полей контракта не заводится. */
  side?: boolean;
  /** Родной поиск в шапке шторки (как «Модели»): натив рисует строку над клавиатурой, ввод
   *  шлёт searchQuery {q}. onSearch получает текст; старые сборки поле игнорируют (веб-поле). */
  search?: { placeholder?: string };
  onSearch?: (q: string) => void;
  /** Верх шторки ТЁМНЫЙ (герой-градиент, как подстраницы «Настроек») — стекло кнопок нативной
   *  шапки стартует тёмным. Не задан/false — светлый верх: стекло стартует по теме кабинета.
   *  Только старт, без мигания «тёмное→светлое»; дальше материал сам адаптируется (владелец
   *  2026-07-26). */
  glassDark?: boolean;
}

interface SlideStackApi {
  available: boolean;
  /** Выехать новой плашкой (натив анимирует). bottom=true — нижний лист (снизу), иначе сбоку.
   *  detent (0..1) — НИЗКИЙ лист доли экрана (подтверждения ~0.4); только с bottom.
   *  deck=true — лист СТОПКОЙ (Н39): экран под ним уходит назад колодой, за ним фирменная
   *  подложка; только с bottom, старые сборки открывают обычный лист.
   *  Возвращает тег плашки — им же адресуется update (см. ниже). */
  push: (panel: SlidePanel, bottom?: boolean, detent?: number, deck?: boolean) => number;
  /** Снять верхнюю плашку родным выездом назад. */
  pop: () => void;
  /** Обновить верхнюю плашку на месте. Без `body` — только шапка (тело сохраняется, состояние
   *  не теряется: внутренняя навигация каталога). С `body` — подменить и содержимое (редактор:
   *  пикер → настройки в ОДНОМ слое). Тот же ref тела в body → React не перемонтирует поддерево.
   *  tag (владелец 2026-08-04): обновлять, только пока вершина — ИМЕННО эта плашка. Нужен
   *  долгоживущим владельцам содержимого (фильтр раздела в шторке): раздел остаётся
   *  смонтированным под открытой поверх карточкой, и его правка иначе ушла бы в чужую шапку. */
  update: (patch: Partial<SlidePanel>, tag?: number) => void;
}

const Ctx = createContext<SlideStackApi | null>(null);

// Модульный реестр тегов ПЛАШЕК (слои-маршруты сюда не попадают): нативная шапка сценария
// должна сниматься, пока сверху плашка (шапка принадлежит экрану сценария, а не плашке).
// Проверка — topIsPanel() на каждый subscribeTopLayer-тик.
const panelTags = new Set<number>();

/** Вершина нативного стека — плашка (не маршрут)? false и при пустом стеке. */
export function topIsPanel(): boolean {
  return panelTags.has(topLayerTag());
}

// Фолбэк вне <NativeLayerHost> (стабильная ссылка): available=false → useSlidePanel уйдёт в drawer.
// Не бросаем — чтобы useSlidePanel можно было безопасно звать в любом компоненте.
const NO_STACK: SlideStackApi = { available: false, push: () => 0, pop: () => {}, update: () => {} };

/** Стопка боковых слоёв-плашек. Вне <NativeLayerHost> — безопасный фолбэк (available=false). */
export function useSlideStack(): SlideStackApi {
  return useContext(Ctx) ?? NO_STACK;
}

/** Открыть панель «как drawer», но в приложении — родным боковым слоем, в вебе — прежним drawer.
 *  Для отдельностоящих шторок (расходы, подключить аккаунт и т.п.): одна замена drawer.open →
 *  panel.open. close снимает верхнее (слой pop / верхний уровень drawer). Вложенную навигацию
 *  в шапке (onNav-перестройка) сюда не тащить — для неё push/update стопки напрямую. */
export function useSlidePanel(): {
  available: boolean;
  open: (p: DrawerPayload) => void;
  /** ЗАКРЫТЬ ВЕРХНЕЕ — ОДИНАКОВО В ПРИЛОЖЕНИИ И В ВЕБЕ (ТЗ WEB-PARITY-3, К3.1, владелец
   *  02.10.2026): в приложении это `pop` одного слоя, в вебе — `drawer.closeLevel`: в окне
   *  больше одного уровня — снимается один верхний, уровень один — закрывается окно.
   *  Раньше в вебе снималось окно ЦЕЛИКОМ, и вложенная страница (триггер мастера, окно
   *  «Сообщение» рассылки) своим «Готово» закрывала весь мастер. */
  close: () => void;
  /** ЗАКРЫТЬ ОКНО ЦЕЛИКОМ, со всеми уровнями — только там, где так нужно по смыслу: работа
   *  в окне закончена и человек уходит (бот создан — список «Боты», переход на другую
   *  страницу, окно переехало в поле). В вебе — `drawer.close`; в приложении — прежний
   *  `pop`: там флоу целиком снимает стопка слоёв (closeCreateToList), а не поверхность. */
  closeAll: () => void;
  /** СНЯТЬ ПОТОК УРОВНЕЙ (05.10.2026, ТЗ «Причины» П1): в вебе — `drawer.closeFrom` (нижний
   *  уровень с одним из имён и всё над ним; «Готово» на странице триггера снимает меню видов
   *  и список ботов под ней, как родная шторка потока). В приложении — `pop` одного слоя:
   *  потоки мастера там ведёт натив (WizardScreen, wizFlowClose). */
  closeFrom: (winKeys: readonly string[], opts?: { lowerKeepDeck?: boolean }) => void;
  /** НАЗАД НА УРОВЕНЬ ВЫШЕ — не закрытие окна целиком. Нужен вложенным поверхностям
   *  (страница кнопки и лист вложений поверх окна «Сообщение»): в приложении это `pop`
   *  стопки, а в вебе — `drawer.back`. От `close` отличается корнем окна: там `back`
   *  идёт ходом `onBack` корня, если он есть, а `close` закрывает окно. */
  back: () => void;
  /** Обновить шапку ОТКРЫТОЙ поверхности на месте (заголовок/подзаголовок) — счётчик
   *  шагов мастера живёт этим. Натив — update стопки, веб — drawer.update. */
  update: (patch: Partial<Pick<DrawerPayload, 'title' | 'subtitle' | 'actionLabel' | 'onAction' | 'actionDisabled' | 'headerRight' | 'headerLeft' | 'sheetDetent'>>) => void;
} {
  const stack = useSlideStack();
  const drawer = useDrawer();
  const open = useCallback(
    (p: DrawerPayload) => {
      if (stack.available) {
        const headerRight =
          p.headerRight ??
          (p.actionLabel ? (
            <KBareButton type="button" className="dr-act" title={p.actionLabel} aria-label={p.actionLabel} onClick={() => p.onAction?.()}>
              ✓
            </KBareButton>
          ) : undefined);
        // sheet:true — нижний лист (выезжает снизу), иначе боковой выезд справа. Направление
        // сохраняем ровно как задал вызывающий флагом sheet. actionLabel/onAction едут в панель
        // отдельно — у листа с нативным верхом кнопку рисует натив (headerRight — DOM-фолбэк).
        stack.push(
          {
            title: p.title,
            subtitle: p.subtitle,
            body: p.body,
            onBack: p.onBack,
            headerRight,
            headerLeft: p.headerLeft,
            actionLabel: p.actionLabel,
            actionIcon: p.actionIcon,
            action2Icon: p.action2Icon,
            action2Label: p.action2Label,
            onAction2: p.onAction2,
            onAction: p.onAction,
            actionMenu: p.actionMenu,
            onMenu: p.onMenu,
            search: p.search,
            onSearch: p.onSearch,
            glassDark: p.glassDark,
            // Окно конструктора: корневой уровень получает ✕ (владелец 13.08.2026).
            side: p.side,
          },
          !!p.sheet,
          p.sheetDetent,
          // СТОПКА (Н39): лист конечного объекта отодвигает экран под собой колодой.
          p.deck,
        );
      } else {
        drawer.open(p);
      }
    },
    [stack, drawer],
  );
  const close = useCallback(() => {
    if (stack.available) stack.pop();
    else if (drawer.closeLevel) drawer.closeLevel();
    else drawer.close();
  }, [stack, drawer]);
  const closeAll = useCallback(() => {
    if (stack.available) stack.pop();
    else drawer.close();
  }, [stack, drawer]);
  const closeFrom = useCallback(
    (winKeys: readonly string[], opts?: { lowerKeepDeck?: boolean }) => {
      if (stack.available) stack.pop();
      else if (drawer.closeFrom) drawer.closeFrom(winKeys, opts);
      else drawer.close();
    },
    [stack, drawer],
  );
  const back = useCallback(() => {
    if (stack.available) stack.pop();
    else if (drawer.back) drawer.back();
    else drawer.close();
  }, [stack, drawer]);
  const update = useCallback(
    (patch: Partial<Pick<DrawerPayload, 'title' | 'subtitle' | 'actionLabel' | 'onAction' | 'actionDisabled' | 'headerRight' | 'headerLeft' | 'sheetDetent'>>) => {
      // Высота листа по содержимому (`sheetDetent`, полушторка подтверждения оплаты) — только веб: у родных
      // слоёв приложения детент задаётся при открытии, а оплаты в приложении нет.
      if (stack.available) {
        const { sheetDetent: _d, ...rest } = patch;
        stack.update(rest);
      } else drawer.update(patch);
    },
    [stack, drawer],
  );
  return { available: stack.available, open, close, closeAll, closeFrom, back, update };
}

// ── ВЕРТИКАЛЬНЫЕ ЛИСТЫ, ИДУЩИЕ СТОПКОЙ ПО ИМЕНИ (владелец 25.08.2026) ────────────────
// Уровни листов приложения: тег родного слоя + имя уровня (winKey). Бухгалтерия ЖИВЁТ В
// МОДУЛЕ, а не в компоненте: вложенный лист открывает СВОЙ экземпляр содержимого (лента
// внутри шторки — это ещё одна лента со своим хуком), а стопка у них одна на всех.
// Снятие слоя чужой рукой (× / свайп / pop) вычёркивает запись через onDismiss.
let sheetLvls: ReadonlyArray<{ tag: number; key: string }> = [];

/**
 * ВЕРТИКАЛЬНАЯ ШТОРКА СО СТОПКОЙ (владелец 25.08.2026, дефект №1 по шторке ленты «шаг с
 * несколькими входами»: в приложении она открылась ВЕБ-листом, а все шторки конструктора
 * там родные).
 *
 * В приложении — РОДНОЙ нижний лист (`push(panel, bottom=true, detent)`): натив рисует
 * лист, его верх и жесты, вложенный лист ложится ПОВЕРХ живого родителя, «‹» и свайп
 * снимают только верхний (pop), а экран под листом уходит назад КОЛОДОЙ (§11 п.18).
 * ДЕТЕНТА У НАБОРА EDITOR_SHEET БОЛЬШЕ НЕТ (владелец
 * 25.08.2026: «открывать повыше, почти до островка») — лист без детента натив ставит на
 * `safeAreaInsets.top + sheetGap`, то есть сразу под вырез (LayerGeom.sheetFrame).
 * ЭТИМ ВХОДОМ ОТКРЫВАЕТСЯ ТОЛЬКО НОВЫЙ КОНТЕКСТ (владелец 25.08.2026, вечер): шторка ленты
 * и окна страницы сценария. Правка блока внутри схемы (и внутри самой шторки ленты) идёт
 * боковым слоем через useEditorPanel — вертикали у неё больше нет. Имя уровня берётся из `winKey` payload'а — ровно как в
 * вебе: имя уже в стопке → снимаем всё, что выше, и остаёмся на нём (дубля не будет).
 * В вебе — прежний `drawer.open(p)` со своим стеком уровней (drawer.tsx, winKey).
 * Вне провайдеров (публичный снимок вне кабинета, админская видео-студия) — available
 * false и open() ничего не делает: открывать негде, но и падать нечему.
 *
 * onBack-реопен родителя НЕ задаём и сюда не пускаем — прямой запрет канона
 * NATIVE-SLIDE-PLAN §11 п.8 (в приложении это выехал бы ДУБЛЬ родителя).
 */
export function useStackedSheet(): { available: boolean; open: (p: DrawerPayload) => void } {
  const stack = useSlideStack();
  const drawer = useDrawerOptional();
  const open = useCallback(
    (p: DrawerPayload) => {
      if (stack.available) {
        const at = p.winKey ? sheetLvls.findIndex((l) => l.key === p.winKey) : -1;
        if (at >= 0) {
          // Этот уровень уже в стопке (лист вёл обратно в шаг, открытый ниже): снимаем
          // слои выше него родным выездом назад и остаёмся на нём — как в вебе.
          const above = sheetLvls.length - 1 - at;
          sheetLvls = sheetLvls.slice(0, at + 1);
          void (async () => {
            for (let i = 0; i < above; i += 1) await popLayer();
          })();
          return;
        }
        let tag = 0;
        tag = stack.push(
          {
            title: p.title,
            ...(p.subtitle ? { subtitle: p.subtitle } : {}),
            body: p.body,
            onDismiss: () => {
              sheetLvls = sheetLvls.filter((l) => l.tag !== tag);
            },
          },
          true,
          p.sheetDetent,
        );
        sheetLvls = [...sheetLvls, { tag, key: p.winKey ?? '' }];
        return;
      }
      drawer?.open(p);
    },
    [stack, drawer],
  );
  return { available: stack.available || !!drawer, open };
}

/** Панель-редактор: drawer-совместимая замена useDrawer для окон конструктора (настройки шага
 *  ↔ пикер ↔ каталог ↔ подключить ↔ триггер-пути). В приложении ведёт СТЕК УРОВНЕЙ окна: каждый
 *  новый уровень — ОТДЕЛЬНЫЙ родной слой; направление берёт из payload (`sheet` → нижний лист,
 *  без него → БОКОВОЙ слой справа: окна конструктора снова горизонтальные, набор EDITOR_SIDE —
 *  владелец 25.08.2026, вечер. Открыт поверх живого листа — натив сам делает такой слой
 *  ВЛОЖЕННЫМ в раму листа, §11 п.10) (канон §11 п.8: вложенность стопкой, не подменой;
 *  родитель жив под слоем, «назад» = pop). Кто новый уровень, а кто обновление на месте, решает
 *  имя `winKey` payload'а — ровно как в вебе (drawer.tsx):
 *   • имени в стеке нет → выехать НОВЫМ слоем поверх;
 *   • имя в стеке есть → снять слои выше него и обновить его содержимое на месте (так
 *     настройки возвращаются после пикера/подключения, без дублей);
 *   • winKey нет вовсе → прежнее поведение: подмена содержимого верхнего уровня.
 *  В вебе — прежний drawer без изменений (стек уровней ведёт он сам).
 *  Возвращает СТАБИЛЬНЫЙ объект (годится в deps useCallback вызывающего). */
export function useEditorPanel(): { open: (p: DrawerPayload) => void; close: () => void } {
  const slide = useSlideStack();
  const drawer = useDrawer();
  const slideRef = useRef(slide);
  slideRef.current = slide;
  const drawerRef = useRef(drawer);
  drawerRef.current = drawer;
  // Уровни окна в приложении: тег родного слоя + имя уровня. Снятие слоя (× / свайп / pop
  // чужой рукой) вычёркивает запись через onDismiss — бухгалтерия не расходится со стеком.
  const levels = useRef<{ tag: number; winKey?: string }[]>([]);
  const apiRef = useRef<{ open: (p: DrawerPayload) => void; close: () => void } | null>(null);
  if (!apiRef.current) {
    apiRef.current = {
      open: (p: DrawerPayload) => {
        const s = slideRef.current;
        if (!s.available) {
          drawerRef.current.open(p);
          return;
        }
        const headerRight =
          p.headerRight ??
          (p.actionLabel ? (
            <KBareButton type="button" className="dr-act" title={p.actionLabel} aria-label={p.actionLabel} onClick={() => p.onAction?.()}>
              ✓
            </KBareButton>
          ) : undefined);
        /** withBack: onBack payload'а уезжает в слой ТОЛЬКО у корневого уровня. У вложенного
         *  «назад» — это pop (родитель жив под слоем); onBack-реопен родителя в приложении
         *  пушил бы ДУБЛЬ — прямой запрет §11 п.8. */
        const mkPanel = (withBack: boolean): SlidePanel => ({
          title: p.title,
          body: p.body,
          ...(withBack && p.onBack ? { onBack: p.onBack } : {}),
          headerRight,
          actionLabel: p.actionLabel,
          actionIcon: p.actionIcon,
          action2Icon: p.action2Icon,
          action2Label: p.action2Label,
          onAction2: p.onAction2,
          onAction: p.onAction,
          search: p.search,
          onSearch: p.onSearch,
          side: p.side,
        });
        const ls = levels.current;
        // Уровень с таким именем уже открыт (или winKey не задан — тогда верхний): снимаем
        // слои выше него родным выездом назад и обновляем его на месте.
        const at = p.winKey ? ls.findIndex((l) => l.winKey === p.winKey) : ls.length - 1;
        if (at >= 0) {
          const above = ls.length - 1 - at;
          const tag = ls[at]!.tag;
          levels.current = ls.slice(0, at + 1);
          void (async () => {
            for (let i = 0; i < above; i += 1) await popLayer();
            // Вершина — не наш уровень (сверху чужая плашка): подменять её содержимое нельзя,
            // выезжаем своим слоем — иначе правка ушла бы в чужую шапку.
            if (topLayerTag() === tag) slideRef.current.update(mkPanel(at === 0), tag);
            else pushLevel(levels.current.length === 0);
          })();
          return;
        }
        pushLevel(ls.length === 0);

        /** Новый уровень окна отдельным боковым слоем (§11 п.8). root — корневой уровень:
         *  ему достаётся onBack payload'а и крестик шапки (side). */
        function pushLevel(root: boolean): void {
          let tag = 0;
          tag = slideRef.current.push(
            {
              ...mkPanel(root),
              onDismiss: () => {
                levels.current = levels.current.filter((l) => l.tag !== tag);
              },
            },
            !!p.sheet,
            p.sheetDetent,
          );
          levels.current = [...levels.current, { tag, ...(p.winKey ? { winKey: p.winKey } : {}) }];
        }
      },
      close: () => {
        const s = slideRef.current;
        if (!s.available) {
          drawerRef.current.close();
          return;
        }
        // Окно закрывается ЦЕЛИКОМ — снимаем все свои уровни (владелец 13.08.2026).
        const n = levels.current.length;
        levels.current = [];
        void (async () => {
          for (let i = 0; i < n; i += 1) await popLayer();
        })();
      },
    };
  }
  return apiRef.current;
}

// «Назад» — ОСТРАЯ СКОБКА ‹, не стрелка с хвостом (владелец 2026-07-23, как в iOS).
const IconBack = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M14.5 5.5 8 12l6.5 6.5" />
  </svg>
);
const IconClose = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export function NativeLayerHost({ children }: { children: React.ReactNode }) {
  const [stack, setStack] = useState<{ id: number; panel: SlidePanel; bottom: boolean; detent?: number; deck?: boolean }[]>([]);
  // Вершина всего нативного стека (плашки + маршруты-слои живут в одном стеке native-layers).
  // Показываем свою плашку ТОЛЬКО если её тег — вершина: иначе сверху маршрут (сценарий) и
  // плашку надо прятать, чтобы натив показал сценарий. Заменяет прежнее скрытие по .cab-focus,
  // которое прятало и плашки, ЛЕГИТИМНО открытые ПОВЕРХ сценария (подключить аккаунт и т.п.).
  const top = useSyncExternalStore(subscribeTopLayer, topLayerTag, () => 0);

  const push = useCallback((panel: SlidePanel, bottom = false, detent?: number, deck?: boolean) => {
    const id = nextLayerTag();
    panelTags.add(id); // реестр плашек — для topIsPanel (нативная шапка сценария)
    void pushLayer(
      () => setStack((s) => [...s, { id, panel, bottom, ...(detent ? { detent } : {}), ...(deck ? { deck } : {}) }]),
      () => {
        panelTags.delete(id);
        setStack((s) => s.filter((x) => x.id !== id));
        panel.onDismiss?.(); // слой снят (× / свайп / pop) — уведомить владельца
      },
      undefined,
      id,
      bottom,
      detent,
      deck,
    );
    return id;
  }, []);
  const pop = useCallback(() => {
    void popLayer();
  }, []);
  const update = useCallback((patch: Partial<SlidePanel>, tag?: number) => {
    setStack((s) => {
      if (!s.length) return s;
      const last = s[s.length - 1]!;
      // Адресный update: вершина уже другая (поверх открыли карточку) — правку роняем.
      if (tag !== undefined && last.id !== tag) return s;
      // Без body — тело сохраняем той же ссылкой (React не перемонтирует). С body — подменяем.
      return [...s.slice(0, -1), { ...last, panel: { ...last.panel, ...patch } }];
    });
  }, []);

  // Нижний лист: сообщаем нативу, прокручено ли тело к верху (свайп-вниз-в-любом-месте). Пока
  // вверху — натив закрывает лист откуда угодно; проскроллено — только за верхний грабер.
  const topEntry = stack.find((e) => e.id === top);
  const topIsBottom = !!topEntry?.bottom;

  // ── Нативный верх шторки (владелец 2026-07-21: у ВСЕХ шторок, и вертикальных, и
  // горизонтальных) ── лист: грабер + «×/← | заголовок | действие»; боковой слой: та же
  // строка на материале. Рисуем нативом, когда шапка ВЫРАЗИМА: правый слот — пусто, текст
  // (actionLabel) или SF-иконка (actionIcon); onBack выразим (ведущая кнопка ←, тап идёт в
  // веб — тот сам зовёт onBack/pop). НЕвыразимый кастомный headerRight — DOM-фолбэк.
  // Функции шапок не теряются: всё, что было в DOM-шапке, переезжает в натив 1:1.
  const sheetHeadOk = (e?: { panel: SlidePanel; bottom: boolean }): boolean =>
    !!e &&
    nativeMethod('configureSheetHeader') && // НЕ по свойству: мост — Proxy (капкан nativeMethod)
    // Слово в ЛЕВОМ слоте («Выбрать всё») родная шапка не выражает — лист остаётся с
    // DOM-шапкой целиком, иначе кнопка потерялась бы совсем.
    !e.panel.headerLeft &&
    (!e.panel.headerRight || e.panel.actionLabel !== undefined || e.panel.actionIcon !== undefined);
  const topNativeSheetHead = sheetHeadOk(topEntry);
  const topEntryRef = useRef(topEntry);
  useEffect(() => {
    topEntryRef.current = topEntry;
  });
  // Режим «без фона» (data-nobg, владелец 2026-07-29): героя-градиента нет, значит и
  // тёмный старт стекла кнопок не нужен. Следим за атрибутом живьём — тумблер щёлкают
  // прямо в открытой шторке «Внешнего вида», её кнопки обязаны посветлеть на месте.
  const [nobg, setNobg] = useState(
    () => typeof document !== 'undefined' && document.documentElement.hasAttribute('data-nobg'),
  );
  useEffect(() => {
    const root = document.documentElement;
    const mo = new MutationObserver(() => setNobg(root.hasAttribute('data-nobg')));
    mo.observe(root, { attributes: true, attributeFilter: ['data-nobg'] });
    return () => mo.disconnect();
  }, []);
  useEffect(() => {
    const br = nativeBridge();
    if (!br || !nativeMethod('configureSheetHeader')) return;
    if (topNativeSheetHead && topEntry) {
      // Ведущая кнопка — как в DOM-шапке: ← при внутренней «назад» или слое над другим,
      // иначе × (закрыть). Сам тап обрабатывает веб (onBack ?? pop) — иконка только вид.
      const idx = stack.findIndex((x) => x.id === topEntry.id);
      // Боковой (горизонтальный) слой — «назад»-скобка, не крестик (владелец 2026-07-23).
      // ИСКЛЮЧЕНИЕ (владелец 13.08.2026): КОРНЕВОЙ уровень окна КОНСТРУКТОРА закрывается
      // крестиком — «✕ только на корне, ← только вглубь». Признак — флаг side у плашки
      // (его ставят окна конструктора) вместе с idx === 0.
      const asBack =
        !!topEntry.panel.onBack || idx > 0 || (!topEntry.bottom && !topEntry.panel.side);
      void br.configureSheetHeader!({
        title: topEntry.panel.title,
        // Серая строка под заголовком (счётчик шагов мастера): старые сборки ключ
        // игнорируют — до сборки Xcode строка видна только в DOM-шапке.
        ...(topEntry.panel.subtitle ? { subtitle: topEntry.panel.subtitle } : {}),
        side: !topEntry.bottom,
        lead: asBack ? 'back' : 'close',
        ...(topEntry.panel.actionLabel ? { actionLabel: topEntry.panel.actionLabel } : {}),
        ...(topEntry.panel.actionIcon ? { actionIcon: topEntry.panel.actionIcon } : {}),
        // Вторая правая кнопка (фильтр рядом с «+»): только сборкам, которые её умеют.
        ...(topEntry.panel.action2Icon && nativeMethod('sheetHeaderAction2')
          ? {
              action2Icon: topEntry.panel.action2Icon,
              ...(topEntry.panel.action2Label ? { action2Label: topEntry.panel.action2Label } : {}),
            }
          : {}),
        // Ключи пунктов уезжают с префиксом sheetmenu: — не пересекаются со служебными
        // sheetClose/sheetAction; обратно снимается в headerAction-обработчике.
        ...(topEntry.panel.actionMenu?.length
          ? { actionMenu: topEntry.panel.actionMenu.map((m) => ({ ...m, key: `sheetmenu:${m.key}` })) }
          : {}),
        ...(topEntry.panel.titleIconPng ? { titleIconPng: topEntry.panel.titleIconPng } : {}),
        ...(topEntry.panel.tabs?.length
          ? { tabs: topEntry.panel.tabs, selectedTab: topEntry.panel.selectedTab ?? '' }
          : {}),
        // Родной поиск шторки (как «Модели»): ключ sheet:search; ввод придёт searchQuery {q}.
        ...(topEntry.panel.search
          ? { search: { key: 'sheet:search', placeholder: topEntry.panel.search.placeholder ?? '' } }
          : {}),
        // Стартовый вид стекла кнопок: тёмный — у шторок с тёмным героем ИЛИ в тёмной теме
        // кабинета (там любой верх тёмный); иначе светлый. Без этого светлые шторки мигали
        // «тёмное→светлое» на открытии (владелец 2026-07-26). В режиме «без фона» героя
        // нет — тёмный старт не нужен (владелец 2026-07-29).
        glassDark:
          (topEntry.panel.glassDark === true && !nobg) ||
          document.documentElement.getAttribute('data-theme') === 'dark',
      });
    } else {
      void br.clearSheetHeader?.();
    }
  }, [topNativeSheetHead, topEntry, stack, nobg]);
  // Тапы нативного верха: ведущая кнопка = внутренняя «назад» панели или снятие слоя
  // (ровно как DOM-lead); кнопка действия зовёт onAction панели.
  useEffect(() => {
    const br = nativeBridge();
    if (!br || !nativeMethod('configureSheetHeader') || !br.addListener) return;
    let sub: { remove: () => void | Promise<void> } | null = null;
    let dead = false;
    // КАПКАН (владелец 2026-07-21, консоль с Mac): у НАСТОЯЩЕГО моста addListener может
    // вернуть хендл БЕЗ .then (не промис) — .then(...) роняет кабинет TypeError'ом.
    // await переваривает и промис, и синхронный хендл.
    void (async () => {
      const s = await br.addListener!('headerAction', (d) => {
        const e = topEntryRef.current;
        if (!e) return;
        if (d?.key === 'sheetClose') {
          if (e.panel.onBack) e.panel.onBack();
          else void popLayer();
        } else if (d?.key === 'sheetAction') e.panel.onAction?.();
        else if (d?.key === 'sheetAction2') e.panel.onAction2?.();
        else if (typeof d?.key === 'string' && d.key.startsWith('sheetmenu:')) e.panel.onMenu?.(d.key.slice(10));
        else if (typeof d?.key === 'string' && d.key.startsWith('sheettab:')) e.panel.onTab?.(d.key.slice(9));
      });
      if (dead) void s.remove();
      else sub = s;
    })();
    return () => {
      dead = true;
      void sub?.remove();
    };
  }, []);
  // Ввод из РОДНОЙ строки поиска шторки (как «Модели»): searchQuery {q} → onSearch верхней плашки.
  // Крестик шлёт q:'' → сброс. Старые сборки события не шлют (поле рисует веб).
  useEffect(() => {
    const br = nativeBridge();
    if (!br || !nativeMethod('configureSheetHeader') || !br.addListener) return;
    let sub: { remove: () => void | Promise<void> } | null = null;
    let dead = false;
    void (async () => {
      const s = await br.addListener!('searchQuery', (d) => {
        topEntryRef.current?.panel.onSearch?.((d as { q?: string })?.q ?? '');
      });
      if (dead) void s.remove();
      else sub = s;
    })();
    return () => {
      dead = true;
      void sub?.remove();
    };
  }, []);
  // Открыт/закрыт родной поиск шторки: класс на <html> прячет ВЕБ-поле пикера (натив даёт своё).
  useEffect(() => {
    const br = nativeBridge();
    if (!br || !nativeMethod('configureSheetHeader') || !br.addListener) return;
    let sub: { remove: () => void | Promise<void> } | null = null;
    let dead = false;
    void (async () => {
      const s = await br.addListener!('searchState', (d) => {
        document.documentElement.classList.toggle('pick-native-search', !!(d as { open?: boolean })?.open);
      });
      if (dead) void s.remove();
      else sub = s;
    })();
    return () => {
      dead = true;
      document.documentElement.classList.remove('pick-native-search');
      void sub?.remove();
    };
  }, []);
  useEffect(() => {
    if (!topIsBottom) return;
    const br = nativeBridge();
    if (!br || !nativeMethod('sheetScrollAtTop')) return;
    const body = Array.from(document.querySelectorAll<HTMLElement>('.native-layer-bottom'))
      .find((el) => el.style.display !== 'none')
      ?.querySelector<HTMLElement>('.dr-body');
    if (!body) return;
    let last = true;
    void br.sheetScrollAtTop!({ atTop: true }); // лист открылся у верха (метод проверен nativeMethod)
    const report = () => {
      const atTop = body.scrollTop <= 1;
      if (atTop === last) return;
      last = atTop;
      void br.sheetScrollAtTop?.({ atTop });
    };
    body.addEventListener('scroll', report, { passive: true });
    return () => body.removeEventListener('scroll', report);
  }, [topIsBottom, top]);

  // ЗНАЧЕНИЕ КОНТЕКСТА — ОДИН ОБЪЕКТ, ПОКА НЕ ПОМЕНЯЛОСЬ СОДЕРЖИМОЕ (ТЗ WEB-CAUSES М42 п.1, замер ОП-14
  // 06.10.2026). Прежде здесь каждый рендер собирался новый объект — а хост перерисовывается с каждой
  // перерисовкой оболочки (тап по вкладке), и все потребители useSlidePanel (каждый раздел стека:
  // «Боты», «Чаты», «Контакты», «Настройки», «Аккаунт» — мимо их memo) рисовались заново в срочном
  // кадре смены вкладки: профиль волокон — 70 % рендера тапа.
  const available = hasNativeLayers();
  const api = useMemo<SlideStackApi>(() => ({ available, push, pop, update }), [available, push, pop, update]);

  return (
    <Ctx.Provider value={api}>
      {children}
      {stack.map(({ id, panel, bottom, detent, deck }, i) => {
        // Видна вершина стека И плашка НОВЕЕ вершины (id монотонный): во время выезда
        // top обновляется только ПОСЛЕ ready (pushLayer), и новая плашка иначе стояла бы
        // display:none все кадры анимации — натив вёз карточку со СТАРЫМ экраном, а контент
        // «подменялся через секунду» (владелец 2026-07-24). Новее top = уже едет — показать.
        const visible = id >= top;
        const lead = panel.onBack ?? pop;
        // Скобка ‹ вместо крестика у ГОРИЗОНТАЛЬНЫХ слоёв (владелец 2026-07-23); крестик —
        // у нижних листов верхнего уровня И у КОРНЕВОГО уровня окна конструктора
        // (владелец 13.08.2026, флаг side; вложенные уровни — скобка).
        const asBack = !!panel.onBack || i > 0 || (!bottom && !panel.side);
        // Нативный верх листа: DOM-грабер/шапочку прячет класс (натив рисует свои).
        const nsheet = sheetHeadOk({ panel, bottom });
        return (
          <div
            key={id}
            className={`native-layer${bottom ? ' native-layer-bottom' : ''}${nsheet ? ' nsheet-native' : ''}${nsheet && panel.tabs?.length ? ' nsheet-tabs' : ''}`}
            style={visible ? undefined : { display: 'none' }}
            /* Вид слоя словом словаря §11.0 (ред. 11, п.10.12): side / deck / half / sheet — как у drawer.tsx. */
            data-layer-kind={bottom ? (detent ? 'half' : deck ? 'deck' : 'sheet') : 'side'}
            role="dialog"
            aria-modal="true"
            aria-label={panel.title}
          >
            <div className="dr-head">
              {panel.headerLeft ?? (
                <KBareButton type="button" className="dr-close" onClick={lead} aria-label={asBack ? 'Назад' : 'Закрыть'}>
                  {asBack ? <IconBack /> : <IconClose />}
                </KBareButton>
              )}
              {panel.subtitle ? (
                <div className="dr-head-t">
                  <h3>{panel.title}</h3>
                  <p>{panel.subtitle}</p>
                </div>
              ) : (
                <h3>{panel.title}</h3>
              )}
              {panel.headerRight ?? <span className="dr-act hidden" aria-hidden="true" />}
            </div>
            <LayerBody visible={visible}>{panel.body}</LayerBody>
          </div>
        );
      })}
    </Ctx.Provider>
  );
}

/**
 * Тело плашки, ПОМНЯЩЕЕ прокрутку (владелец 2026-08-06, Т2: «после закрытия превью
 * нижняя планка дёргается»). Пока поверх открыт нативный лист, слой стоит display:none —
 * браузер обнуляет scrollTop, и на возврате тело прыгало к началу вместе с прибитой
 * планкой. Держим последнюю позицию у себя и возвращаем её, когда слой снова виден.
 */
function LayerBody({ visible, children }: { visible: boolean; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Читать scrollTop в момент скрытия поздно: display:none уже применён и вернёт 0 —
  // поэтому позицию копим на каждом скролле, пока слой виден.
  const pos = useRef(0);
  useEffect(() => {
    const el = ref.current;
    if (!visible || !el || !pos.current) return;
    if (Math.abs(el.scrollTop - pos.current) > 1) el.scrollTop = pos.current;
  }, [visible]);
  return (
    <div className="dr-body" ref={ref} onScroll={(e) => { pos.current = e.currentTarget.scrollTop; }}>
      {children}
    </div>
  );
}
